/**
 * @file useBinLabels.ts
 * @description Печать этикеток на странице «Ячейки» (phase19.22 шаг 5):
 *   право, отметки ячеек, журнал печати, открытие диалога и шторки истории.
 *
 * Вынесено из страницы (SRP): страница раскладывает блоки, хук держит
 * состояние печати. Право — как у сервера (`label-access.ts`): печать и
 * журнал доступны при `SHOP_WMS_VIEW` или `SHOP_WMS_MANAGE`. Без права
 * галочек и полос выбора нет вовсе (`selection === null`).
 */

import { useRef, useState } from "react";

import { useBinLabelJobs } from "./useBinLabelJobs";
import { useBinSelection } from "./useBinSelection";
import { useHasPermission } from "../../../auth/hooks";
import type { StorageBinDto } from "../../../lib/api-bins";

export function useBinLabels(bins: StorageBinDto[], warehouseId: string, supportsBins: boolean) {
  const canView = useHasPermission("SHOP_WMS_VIEW");
  const canManage = useHasPermission("SHOP_WMS_MANAGE");
  const canPrint = (canView || canManage) && supportsBins && !!warehouseId;
  const selection = useBinSelection(bins, warehouseId);
  const jobs = useBinLabelJobs(warehouseId, canPrint);
  const [printOpen, setPrintOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Печать удалась — по закрытию диалога снимаем выбор: работа сделана.
  const printed = useRef(false);

  return {
    canPrint,
    selection: canPrint ? selection : null,
    jobs,
    printOpen,
    openPrint: () => {
      printed.current = false;
      setPrintOpen(true);
    },
    setPrintOpen: (open: boolean) => {
      setPrintOpen(open);
      if (!open && printed.current) selection.clear();
    },
    onPrinted: () => {
      printed.current = true;
      jobs.reload();
    },
    historyOpen,
    setHistoryOpen,
  };
}

export type BinLabels = ReturnType<typeof useBinLabels>;
